// The page side of the preview frame. Every 3D preview that runs asset code (the asset page, the avatar creator, the
// review queue's baked model) lives in a sandboxed iframe with an opaque origin: sandbox="allow-scripts" and never
// allow-same-origin, served with a strict CSP (serve.ts). The frame can't read this page's storage or DOM and never gets
// a session token; it gets params and package file paths by postMessage, and only expected shapes come back.

import { h } from "./dom.ts";
import { FRAME_PATH, parseFromFrame, type AvatarSpecMsg, type FromFrame, type ToFrame } from "./frame/protocol.ts";
import type { Manifest, Stats, Value } from "./store.ts";

/** Where other packages (the standard body, wearables) come from: the site's read-only mount of the starter set. */
export const PACKAGES = "/store/packages/";

/** Mount a preview frame in `stage`; messages to it wait until it says it's ready. */
export function previewFrame(stage: HTMLElement, label: string, onMessage: (m: FromFrame) => void): { send(m: ToFrame): void } {
  const frame = h("iframe", { src: FRAME_PATH, sandbox: "allow-scripts", class: "preview-frame", title: label, referrerpolicy: "no-referrer", loading: "eager" });
  stage.prepend(frame);
  let ready = false;
  const queue: ToFrame[] = [];
  const post = (m: ToFrame) => frame.contentWindow?.postMessage(m, "*"); // an opaque origin can only be addressed as "*"; nothing sent is secret
  const listener = (e: MessageEvent) => {
    if (!frame.isConnected) return window.removeEventListener("message", listener);
    if (e.source !== frame.contentWindow || e.origin !== "null") return;
    const m = parseFromFrame(e.data);
    if (!m) return;
    if (m.t === "ready") {
      ready = true;
      for (const q of queue.splice(0)) post(q);
    }
    onMessage(m);
  };
  window.addEventListener("message", listener);
  return {
    send(m) {
      if (ready) post(m);
      else queue.push(m);
    },
  };
}

export interface PreviewOpts {
  /** package files path, ending in "/" (the store's files route or the mount) */
  base: string;
  manifest: Manifest;
  /**
   * "live": run the asset with params; "baked": the default bake only, no asset code; "preview": the store's public
   * low-detail model (paid assets; `base` is the site's /api/store-preview/<id>/<version>/), no asset code either
   */
  mode: "live" | "baked" | "preview";
  /** "baked" mode: the model's path when it isn't at base + bake.default (a signed download) */
  bakedUrl?: string;
  onStats?: (s: Stats) => void;
  onNote?: (text: string) => void;
}

export function startAssetPreview(stage: HTMLElement, o: PreviewOpts): { show(params: Record<string, Value>): void } {
  const f = previewFrame(stage, `3D preview of ${o.manifest.title ?? o.manifest.id}`, (m) => {
    if (m.t === "note") o.onNote?.(m.text);
    else if (m.t === "stats") o.onStats?.(m.stats);
  });
  f.send({ t: "init", kind: "asset", mode: o.mode, base: o.base, manifest: o.manifest, bakedUrl: o.bakedUrl, bodyBase: PACKAGES });
  return { show: (params) => f.send({ t: "params", params }) };
}

export function startAvatarPreview(stage: HTMLElement): { show(spec: AvatarSpecMsg): void; focus(view: "full" | "face"): void } {
  const note = (text: string) => {
    const p = stage.querySelector(".stage-note");
    if (p) p.textContent = text;
  };
  const f = previewFrame(stage, "Avatar preview", (m) => {
    if (m.t === "note") note(m.text);
  });
  f.send({ t: "init", kind: "avatar", packages: PACKAGES });
  return { show: (spec) => f.send({ t: "avatar", spec: structuredClone(spec) }), focus: (view) => f.send({ t: "focus", view }) };
}
